import React, { useEffect, useState } from "react";
import { useQuill } from "./quill-editor";

import "quill/dist/quill.snow.css";
import "./rich-text-editor.scss";

interface RichTextEditorProps {
  placeholder?: string;
  name: string;
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  isValid?: boolean;
  isInvalid?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  placeholder,
  name,
  value,
  onChange,
  required = false,
  readOnly = false,
  disabled = false,
  isValid,
  isInvalid,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const toolbarModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["clean"],
    ],
  };

  const textFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "align",
    "color",
    "background",
  ];

  const { quill, quillRef } = useQuill({
    modules: toolbarModules,
    formats: textFormats,
    placeholder,
    readOnly,
  });

  // Sync initial or external value updates safely
  useEffect(() => {
    if (quill) {
      const current = quill.root.innerHTML;
      if (value !== current) {
        quill.clipboard.dangerouslyPasteHTML(value || "");
      }
    }
  }, [quill, value]);

  // Handle text changes and focus state
  useEffect(() => {
    if (!quill) return;

    const handleChange = () => {
      const html = quill.root.innerHTML;
      const text = quill.getText().trim();
      onChange({ target: { name, value: text ? html : "" } });
    };

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    quill.on("text-change", handleChange);
    quill.root.addEventListener("focus", handleFocus);
    quill.root.addEventListener("blur", handleBlur);

    return () => {
      quill.off("text-change", handleChange);
      quill.root.removeEventListener("focus", handleFocus);
      quill.root.removeEventListener("blur", handleBlur);
    };
  }, [quill, name, onChange]);

  const getBorderStyles = () => {
    if (isInvalid) {
      return {
        borderColor: "#dc3545",
        boxShadow: isFocused ? "0 0 0 0.25rem rgba(220,53,69,.25)" : "none",
      };
    }
    if (isValid) {
      return {
        borderColor: "#198754",
        boxShadow: isFocused ? "0 0 0 0.25rem rgba(25,135,84,.25)" : "none",
      };
    }
    return {
      borderColor: isFocused ? "#86b7fe" : undefined,
      boxShadow: isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
    };
  };

  return (
    <div
      className={` ${isValid
          ? "border-green-500"
          : isInvalid
            ? "border-red-500"
            : "border-gray-300"
        }`}
      style={{
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
        borderRadius: "10px",
        transition:
          "border-color .15s ease-in-out, box-shadow .15s ease-in-out",
        ...getBorderStyles(),
      }}
    >
      <div
        ref={quillRef}
        onPasteCapture={(event: React.ClipboardEvent<HTMLDivElement>) => {
          if (!quill) return;

          const pastedText = event?.clipboardData?.getData("text")?.trim() ?? "";
          const selection = quill?.getSelection(true);
          if (!selection) return;

          event?.preventDefault();
          quill?.deleteText(selection.index, selection.length, "user");
          quill?.insertText(selection.index, pastedText, "user");
          quill?.setSelection(
            selection?.index + pastedText?.length,
            0,
            "silent",
          );
        }}
        style={{
          minHeight: 200,
          borderBottomLeftRadius: "10px",
          borderBottomRightRadius: "10px",
        }}
      />
    </div>
  );
};

export default RichTextEditor;
